import React, { useEffect, useMemo, useRef } from 'react';

import { Box, Typography, inputBaseClasses } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterJiraStatus, resolveJiraStatusFilter } from '~/components/JiraStatus';
import { Loader } from '~/components/Loader';
import { Page } from '~/components/Page';
import { PageFetcher } from '~/components/PageFetcher';
import { Retry } from '~/components/Retry';
import { useMarketParam } from '~/contexts/UrlParamsContext';
import { mapJiraRequest } from '~/controllers/mapping/requests';
import { getRequests } from '~/controllers/requests';
import { useJiraConfigQuery } from '~/hooks/useJiraConfigQuery';
import { useJiraProjectConfig } from '~/hooks/useJiraProjectConfig';
import { useStatusQuery } from '~/hooks/useStatusQuery/useStatusQuery';
import { RequestItem } from '~/pages/RequestsPage/RequestItem';

import { RequestsHeader } from './RequestsHeader';
import { SearchBar } from './SearchBar';
import { useRequestsSearchParams } from './useRequestsSearchParams';

export const RequestsPage = React.memo(function RequestsPage() {
  const market = useMarketParam();
  const { data: config, isError: isJiraConfigError, refetch: refetchJiraConfig } = useJiraConfigQuery();
  const [{ search, status, statusCategory }, { handleSearchChange, handleStatusChange }] = useRequestsSearchParams();
  const { data: jiraProjectConfig } = useJiraProjectConfig();
  const { isSuccess: isStatusLoaded, data: statusList } = useStatusQuery({ typeName: jiraProjectConfig.requestTypeName });
  const statusQuery = resolveJiraStatusFilter(statusCategory, status, statusList);
  const statusSelection = useMemo(() => {
    return statusCategory ? [statusCategory] : status
  }, [status, statusCategory]);

  const {
    data,
    isFetching,
    isFetched,
    isError,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery(
    ['getRequests', { market, search, status: statusQuery, jiraProjectConfig }],
    getRequests,
    {
      enabled: isStatusLoaded && !!jiraProjectConfig,
      select: ({ pages, pageParams }) => {
        return {
          pageParams,
          pages: pages.map(page => ({ ...page, requests: config ? page.issues.map(issue => mapJiraRequest(issue, config)) : [] })),
        };
      },
      getNextPageParam: (lastPage) => {
        const startAt = lastPage.startAt + lastPage.maxResults;
        if (startAt < lastPage.total) {
          return {
            startAt,
          }
        }
      },
    });
  const { pages = [] } = data || {};
  const lastPage = pages[pages.length - 1];
  const isLast = lastPage && lastPage.startAt + lastPage.maxResults >= lastPage.total;
  
  // Track previous page count to detect new pages
  const prevPageCountRef = useRef(0);
  
  // Deduplicate requests at data level before mapping to React elements
  const allRequests = pages.reduce((prev, page) => {
    prev.push(...page.requests);
    return prev;
  }, [] as typeof pages[0]['requests']);
  
  const uniqueRequests = allRequests.filter((obj, index, self) =>
    index === self.findIndex((el) => el.requestId === obj.requestId)
  );
  
  // Log when pages change and data is loaded
  useEffect(() => {
    if (pages.length > 0 && uniqueRequests.length > 0) {
      const currentPageCount = pages.length;
      const prevCount = prevPageCountRef.current;
      
      // Only log if page count actually changed
      if (currentPageCount !== prevCount) {
        prevPageCountRef.current = currentPageCount;
        
        // Delay logging to ensure components have rendered
        setTimeout(() => {
          if (prevCount === 0 && currentPageCount === 1) {
            // Initial load
            console.log('=== INITIAL PAGE LOAD (Objects Rendered) ===');
            console.log('Total pages loaded:', currentPageCount);
            console.log('Items in first page:', pages[0].requests.length);
            console.log('First page data:', pages[0].requests.map(r => ({ id: r.requestId, title: r.title })));
            console.log('Total unique items:', uniqueRequests.length);
            console.log('All unique items:', uniqueRequests.map(r => ({ id: r.requestId, title: r.title })));
          } else if (currentPageCount > prevCount) {
            // New page loaded via infinite scroll
            const newPageIndex = currentPageCount - 1;
            const newPage = pages[newPageIndex];
            console.log('=== INFINITE SCROLL - NEW PAGE LOADED (Objects Rendered) ===');
            console.log('Page number:', currentPageCount);
            console.log('New page startAt:', newPage.startAt);
            console.log('New items in this page:', newPage.requests.length);
            console.log('New page data:', newPage.requests.map(r => ({ id: r.requestId, title: r.title })));
            console.log('Total unique items after load:', uniqueRequests.length);
            console.log('All unique items:', uniqueRequests.map(r => ({ id: r.requestId, title: r.title })));
          }
        }, 200); // 200ms delay to ensure render completes
      }
    }
  }, [pages, uniqueRequests]);
  
  const requests = uniqueRequests.map(request => {
    return <RequestItem key={request.requestId} request={request} />;
  });

  const addPageFetcher = requests.length && !isLast;
  if (addPageFetcher) {
    // Add fetcher at the very end of the list
    // It will trigger when the last item becomes visible
    requests.push(<PageFetcher key={`fetcher-${requests.length}`} fetch={fetchNextPage} />);
  }

  return (
    <Page>
      <Box sx={{
        bgcolor: ({ palette }) => palette.background.default,
      }}>
        <Box sx={{ my: '10px' }}>
          <RequestsHeader />
        </Box>
        {!isJiraConfigError && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ m: '8px 30px 18px 30px', minWidth: '1380px', alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1">
                Requests
              </Typography>
              <Box key={market} sx={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }} >
                <FilterJiraStatus
                  value={statusSelection}
                  onChange={handleStatusChange}
                  typeName={jiraProjectConfig.requestTypeName}
                  sx={{
                    [`.${inputBaseClasses.input}`]: {
                      background: ({ palette }) => palette.background.paper,
                    },
                  }}
                />
                <SearchBar
                  value={search}
                  onSearch={handleSearchChange}
                  placeholderText="Type Vehicle Title"
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {isJiraConfigError && <Retry retry={refetchJiraConfig} sx={{ margin: 'auto', transform: 'translateY(-50%)' }}>the markets</Retry>}
      {!isJiraConfigError && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'auto',
          height: '100%',
          pb: '50px',
          '> div': {
            width: '100%',
            maxWidth: '1380px',
          },
        }}>
          
          {isFetching && <Loader variant={requests.length > 0 && !isError ? undefined : 'absolute-center'} />}
          {!isFetching && isFetched && !isError && !requests.length && (
            <Typography sx={{ textAlign: 'center', verticalAlign: 'middle', lineHeight: 'calc(100vh - 300px)' }}>
              No requests found
            </Typography>
          )}
          {!isError ? requests : null}
          {/* {isFetching && <Loader variant={requests.length > 0 && !isError ? undefined : 'absolute-center'} />} */}
          {isError && !isFetching && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%' }}>
              <Retry retry={refetch}>the requests</Retry>
            </Box>
          )}
        </Box>
      )}
    </Page>
  );
});
